import React, { Suspense, lazy, useEffect } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { Route, Routes } from 'react-router-dom'

import { GrowthBookProvider } from '@growthbook/growthbook-react'
import { polyfill as smoothScrollPolyfill } from 'smoothscroll-polyfill'
import Swiper, { Autoplay, Pagination } from 'swiper'
import 'swiper/css'
import 'swiper/css/pagination'

import { I18nInterceptorProvider } from 'components/I18nInterceptor'
import { LayoutProvider } from 'components/LayoutProvider'
import { MetaInfo } from 'components/MetaInfo'
import { Spinner } from 'components/Spinner'

import {
  selectConfig,
  selectCurrentVariant,
  selectLanguage,
} from 'root-redux/selects/common'
import { selectUserStatus } from 'root-redux/selects/user'

import {
  growthBookInstance,
  prefetchGrowthBookPayload,
} from 'hooks/useGrowthBookInitialization'
import { useInContextInitialization } from 'hooks/useInContextInitialization'
import { useInitAppConfig } from 'hooks/useInitAppConfig'
import { useLanguage } from 'hooks/useLanguage'
import { useScrollFix } from 'hooks/useScrollFix'
import { useSentryScope } from 'hooks/useSentryScope'
import { useSetUserStatus } from 'hooks/useSetUserStatus'
import { useSetUuid } from 'hooks/useSetUuid'
import { useSetVariant } from 'hooks/useSetVariant'
import { useStuckWatchdog } from 'hooks/useStuckWatchdog'

import { FunnelErrorCode } from 'helpers/funnelErrors'
import { setViewportFullHeight } from 'helpers/setViewportFullHeight'

import { browserHistory } from 'browser-history'
import { TOU_PP_COOKIE_LINKS } from 'root-constants'

import { CustomRouter } from './CustomRouter'

const TermsOfUse = lazy(() =>
  import('pages/TermsOfUse').then((m) => ({ default: m.TermsOfUse })),
)
const PrivacyPolicy = lazy(() =>
  import('pages/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy })),
)
const CookiePolicy = lazy(() =>
  import('pages/CookiePolicy').then((m) => ({ default: m.CookiePolicy })),
)

setViewportFullHeight()
smoothScrollPolyfill()

Swiper.use([Pagination, Autoplay])

export const App: React.FC = () => {
  const userStatus = useSelector(selectUserStatus, shallowEqual)
  const variant = useSelector(selectCurrentVariant, shallowEqual)
  const config = useSelector(selectConfig)
  const language = useSelector(selectLanguage)

  useInitAppConfig()
  useSetVariant()
  useLanguage()
  useSetUuid()
  useSetUserStatus()
  useScrollFix()
  useInContextInitialization()
  useSentryScope()

  const isAppReady = userStatus && variant && config && language
  const isStaticPage = TOU_PP_COOKIE_LINKS.includes(window.location.pathname)

  useStuckWatchdog({
    isReady: isStaticPage || !!isAppReady,
    code: FunnelErrorCode.FUNNEL_APP_READY_TIMEOUT,
    extra: {
      hasUserStatus: !!userStatus,
      hasVariant: !!variant,
      hasConfig: !!config,
      hasLanguage: !!language,
    },
  })

  useEffect(() => {
    if (isStaticPage) return

    prefetchGrowthBookPayload()
  }, [isStaticPage])

  return (
    <CustomRouter history={browserHistory}>
      <MetaInfo />

      <Suspense fallback={<Spinner />}>
        {isStaticPage ? (
          <Routes>
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-use" element={<TermsOfUse />} />
            <Route path="cookie-policy" element={<CookiePolicy />} />
          </Routes>
        ) : (
          <GrowthBookProvider growthbook={growthBookInstance}>
            {isAppReady ? (
              <I18nInterceptorProvider>
                <LayoutProvider />
              </I18nInterceptorProvider>
            ) : (
              <Spinner />
            )}
          </GrowthBookProvider>
        )}
      </Suspense>
    </CustomRouter>
  )
}
